import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorSchema = z.object({ message: z.string() });

// What can be the *target* of a mention — i.e. the thing being linked to
// from inside another file/project body. Files retain the `doc` kind in
// API payloads for compatibility with the existing storage model.
export const mentionTargetKindValues = ['thread', 'plan', 'doc', 'user'] as const;
export const mentionTargetKindSchema = z.enum(mentionTargetKindValues);

export const mentionResultSchema = z.object({
  kind: mentionTargetKindSchema,
  id: z.string().min(1),
  title: z.string().min(1),
  href: z.string().min(1),
});

export const mentionSearchResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('mention'),
  objects: z.array(mentionResultSchema),
});

export const mentionSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(50),
  kinds: z
    .string()
    .optional()
    .transform((value) => {
      if (!value) return undefined;
      const parts = value
        .split(',')
        .map((kind) => kind.trim())
        .filter((kind) => kind.length > 0);
      return parts.length > 0 ? parts : undefined;
    })
    .pipe(z.array(mentionTargetKindSchema).optional()),
});

export const mentionsContract = c.router({
  searchMentions: {
    method: 'GET',
    path: '/mentions/search',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: mentionSearchQuerySchema,
    responses: {
      200: mentionSearchResponseSchema,
      401: errorSchema,
      422: errorSchema,
    },
    summary:
      'Search threads, plans, files, and users by title/name for @-mention typeahead. Org-scoped, max 10 results across all kinds.',
  },
});

// Convenience alias kept for the editor extension where "kind" reads more
// naturally than "target_kind".
export type MentionKind = z.infer<typeof mentionTargetKindSchema>;
export type MentionResult = z.infer<typeof mentionResultSchema>;
export type MentionSearchResponse = z.infer<typeof mentionSearchResponseSchema>;
export type MentionSearchQuery = z.infer<typeof mentionSearchQuerySchema>;
